/**
 * Shared formatting helpers for the Contract Details drawer and its
 * PDF / Excel exporters.
 */

export const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(value);

/**
 * Formats a `"<from> – <to>"` period string for display.
 *
 * Accepts parts in either format:
 *   - ISO `YYYY-MM-DD`  (produced by the BE mapper from WSGetContractInfo)
 *   - Legacy `MM/DD/YY` (kept for backward compatibility with older fixtures)
 *
 * Sentinels:
 *   - `0000-00-00`  → "ongoing"   (upstream uses this for open-ended contracts)
 *   - `"ongoing"`   → "ongoing"
 */
export const formatPeriodDate = (period: string): string => {
    const parts = period.split(' – ');
    const formatted = parts.map((part) => {
        const trimmed = part.trim();
        if (trimmed.toLowerCase() === 'ongoing' || trimmed === '0000-00-00') return 'ongoing';

        const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (iso) {
            const [, yyyy, mm, dd] = iso;
            const date = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }

        const legacy = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
        if (legacy) {
            const [, mm, dd, yy] = legacy;
            const date = new Date(2000 + parseInt(yy), parseInt(mm) - 1, parseInt(dd));
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }

        return trimmed;
    });
    return formatted.join(' – ');
};
